import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { recordingsApi } from '@api/recordings';
import { HttpError } from '@api/http';
import { formatLocalYmd, getViewerUtcOffsetMinutes } from '@lib/date-ymd';
import type { CameraTimeline } from '@api/types';

const TODAY_POLL_MS = 30_000;

interface UseTimelineState {
  data: CameraTimeline | null;
  isLoading: boolean;
  error: string | null;
}

interface UseTimelineResult extends UseTimelineState {
  date: string;
  setDate: (date: string) => void;
  refresh: () => void;
}

export function useTimeline(cameraId: number, enabled = true): UseTimelineResult {
  const [date, setDate] = useState(() => formatLocalYmd(new Date()));
  const [state, setState] = useState<UseTimelineState>({
    data: null,
    isLoading: true,
    error: null,
  });
  const [reloadGeneration, setReloadGeneration] = useState(0);
  /** When true, the next fetch is a background poll (no full-screen loading state). */
  const quietNextFetchRef = useRef(false);

  const query = useMemo(
    () => ({
      date,
      utcOffsetMinutes: getViewerUtcOffsetMinutes(),
    }),
    [date],
  );

  const refresh = useCallback(() => {
    quietNextFetchRef.current = false;
    setState((s) => ({ ...s, isLoading: true }));
    setReloadGeneration((g) => g + 1);
  }, []);

  const refreshQuiet = useCallback(() => {
    quietNextFetchRef.current = true;
    setReloadGeneration((g) => g + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (date !== formatLocalYmd(new Date())) return undefined;
    const id = window.setInterval(() => {
      refreshQuiet();
    }, TODAY_POLL_MS);
    return () => window.clearInterval(id);
  }, [date, enabled, refreshQuiet]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (date === formatLocalYmd(new Date())) refreshQuiet();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [date, enabled, refreshQuiet]);

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, isLoading: false, error: null });
      return undefined;
    }
    const controller = new AbortController();
    const quiet = quietNextFetchRef.current;
    quietNextFetchRef.current = false;

    if (!quiet) {
      setState((s) => ({ ...s, isLoading: true, error: null }));
    }

    recordingsApi
      .getTimeline(cameraId, query, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ data, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load playback timeline';
        setState((s) => ({ data: s.data, isLoading: false, error: message }));
      });

    return () => controller.abort();
  }, [cameraId, enabled, query, reloadGeneration]);

  return { ...state, date, setDate, refresh };
}
